/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';

import { EpisodeInfo, SeasonInfo } from 'client-code/episode-guide';
import { PlayableVodItem, VodItemKernelType } from 'client-code/vod-browse';

import { DeviceModel, Settings } from '../../../core/device';
import { Locale, LocaleKeys } from '../../../core/locale';
import { ScreenControllerTypes, ScreenTaskControllerTypes } from '../../../core/navigation';
import { SharedPrefConstants, SharedPreferences } from '../../../core/storage';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { TListViewType } from '../../../core/types';
import { ListTypes } from '../../../core/utils';
import { setListEntryPosition } from '../../../store/list/list.actions';
import { TListViewScreen } from '../../../store/listscreenview';
import { setListLimitActions } from '../../../store/listscreenview/listviewscreen.actions';
import { ClockTypes } from '../../Clock';
import { List, ScreenHeader } from '../../common';
import { VodPreview } from '../../common/VodPreview/VodPreview';
import { VodScreenType } from '../../common/VodPreview/VodPreviewProps';
import { useScreenController } from '../../hooks';
import OnDemandListItem from './OnDemandListItem';
import { OnDemandListProps } from './OnDemandListProps';

import './OnDemandList.scss';

// Constants
const EXTRA_SEASON_VALUE = -1;

// Types
interface SeasonGroup {
    seasonTitle: string;
    seasonNumber: number;
    episodes: EpisodeInfo[];
    isHeader: true;
}

type ListItem = EpisodeInfo | SeasonGroup | any;

// Utility functions
const getSeasonKey = (seasonNumber: number): string =>
    seasonNumber && seasonNumber <= EXTRA_SEASON_VALUE
        ? `${Locale.getString(LocaleKeys.EXTRA)}`
        : `${Locale.getString(LocaleKeys.SEASON)} ${seasonNumber}`;

const sortSeasons = (a: SeasonGroup, b: SeasonGroup): number => {
    if (a.seasonTitle === `${Locale.getString(LocaleKeys.EXTRA)}`) return 1;
    if (b.seasonTitle === `${Locale.getString(LocaleKeys.EXTRA)}`) return -1;
    return a.seasonNumber - b.seasonNumber;
};

const sortEpisodes = (a: EpisodeInfo, b: EpisodeInfo): number =>
    (a.episodeNum || 0) - (b.episodeNum || 0);

const isSeasonHeader = (item: ListItem): item is SeasonGroup => 'isHeader' in item;

// Convert EpisodeInfo to PlayableVodItem with proper kernel structure
const createPlayableVodItemFromEpisode = (
    episode: EpisodeInfo,
    partnerId: string,
): PlayableVodItem => {
    return {
        digObjectId: episode.contentId || '',
        vodItemKernel: {
            vodItemKernelType: VodItemKernelType.ContentVodItemKernel,
            contentId: episode.contentId || '',
            title: episode.title || '',
            partnerId,
            // Map episode properties to kernel properties
            ...(episode as any), // Spread episode properties for compatibility
        },
    } as unknown as PlayableVodItem;
};

const OnDemandList: FunctionComponent<OnDemandListProps> = ({
    episodes,
    seasonData,
    title,
    isLoading,
    ghostMode,
}): JSX.Element => {
    const [focusedVodItem, setFocusedVodItem] = useState<PlayableVodItem>();
    const [lastSeenSeason, setLastSeenSeason] = useState<number | null>(null);
    const listState = useTivoSelector((state: RootState) => state.list);

    const itemsListEntry = useTivoSelector(
        (state: RootState) => state.list[ListTypes.ON_DEMAND_LIST],
    );
    const itemPosition = itemsListEntry?.position;
    const ListViewScreenIns = useScreenController<TListViewScreen>({
        identifier: 'ListViewScreen',
        properties: {
            screenController: ScreenControllerTypes.LIST_VIEW_SCREEN,
            taskController: ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK,
        },
    });
    const dispatch = useTivoDispatch();

    const isVideoWindowEnabled = useMemo(() => new Settings().isVideoWindowEnabled(), []);
    const partnerId = (ListViewScreenIns?.screen?.getProperties() as any)?.partnerId || '';

    // Group episodes by season and create flattened list with headers
    const { flattenedList, sortedSeasons } = useMemo(() => {
        if (!episodes?.length) {
            return { flattenedList: [], sortedSeasons: [] };
        }
        const seasonDataMap = new Map<number, SeasonInfo>();
        if (seasonData?.seasons) {
            seasonData.seasons.forEach((season: SeasonInfo) => {
                seasonDataMap.set(season?.seasonOrYear, season);
            });
        }

        const grouped = episodes.reduce(
            (acc, episode) => {
                let seasonNumber = episode?.seasonOrYear;

                if (seasonNumber === undefined) {
                    seasonNumber = EXTRA_SEASON_VALUE;
                } else if (seasonData?.seasons) {
                    const seasonInfo = seasonDataMap.get(seasonNumber);

                    if (seasonInfo && seasonInfo.seasonOrYear === EXTRA_SEASON_VALUE) {
                        seasonNumber = EXTRA_SEASON_VALUE;
                    } else if (!seasonInfo) {
                        const extraSeason = seasonDataMap.get(EXTRA_SEASON_VALUE);
                        if (extraSeason && extraSeason.episodeCount > 0) {
                            seasonNumber = EXTRA_SEASON_VALUE;
                        }
                    }
                }

                const seasonKey = getSeasonKey(seasonNumber);
                if (!acc[seasonKey]) {
                    acc[seasonKey] = {
                        seasonTitle: seasonKey,
                        seasonNumber,
                        episodes: [],
                        isHeader: true as const,
                    };
                }
                acc[seasonKey].episodes.push(episode);
                return acc;
            },
            {} as Record<string, SeasonGroup>,
        );

        // Sort seasons and create flattened list with smart header logic
        const seasonsList = Object.values(grouped).sort(sortSeasons);
        const result: ListItem[] = [];
        let currentLastSeen = lastSeenSeason;

        seasonsList.forEach((season) => {
            const sortedEpisodes = season.episodes.sort(sortEpisodes);

            // Only add header if this season hasn't been seen before
            if (currentLastSeen !== season.seasonNumber) {
                result.push(season);
                currentLastSeen = season.seasonNumber;
            }

            // Always add the episodes
            result.push(...sortedEpisodes);
        });

        if (!result[0].isHeader) {
            const listSharedPreferenceEditor = DeviceModel.getSharedPreferences(
                SharedPrefConstants.LIST_SCREEN_PREF_KEY,
            ).edit();
            listSharedPreferenceEditor.putNumber(SharedPrefConstants.LISTVIEW_HIGHLIGHT_INDEX, 0);
            listSharedPreferenceEditor.apply();
        }

        return { flattenedList: result, sortedSeasons: seasonsList };
    }, [episodes, seasonData, lastSeenSeason]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const listType: TListViewType = 'onDemand';
        const onDemandSharedPref = new SharedPreferences(
            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
        ).edit();

        const selectedItems = {
            flattenedList,
            type: listType,
        };
        onDemandSharedPref.putJson(
            SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION,
            selectedItems as unknown as any,
        );

        const currentPosition = listState[ListTypes.ON_DEMAND_LIST]?.position ?? 0;
        const currentItem = flattenedList[currentPosition];

        // Only set focused item if it's not a season header and exists
        if (currentItem && !isSeasonHeader(currentItem)) {
            // Convert EpisodeInfo to PlayableVodItem with proper kernel structure
            const vodItem = createPlayableVodItemFromEpisode(currentItem, partnerId);
            setFocusedVodItem(vodItem);
        } else {
            setFocusedVodItem(undefined);
        }
        const lastSeason = sortedSeasons[sortedSeasons?.length - 1];
        setLastSeenSeason(lastSeason?.seasonNumber);
        dispatch(setListLimitActions(flattenedList?.length - 1));
        if (sortedSeasons.length > 0) {
            const lastSeasonDisplayed = sortedSeasons[sortedSeasons.length - 1];
            setLastSeenSeason(lastSeasonDisplayed.seasonNumber);
        }

        onDemandSharedPref.commit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flattenedList, listState]);

    useEffect(() => {
        const highlight =
            DeviceModel.getSharedPreferences(SharedPrefConstants.LIST_SCREEN_PREF_KEY).getNumber(
                SharedPrefConstants.LISTVIEW_HIGHLIGHT_INDEX,
            ) ??
            ListViewScreenIns?.screen?.getProperties()?.highlight ??
            (flattenedList && flattenedList?.findIndex((item) => !('isHeader' in item)));
        dispatch(setListEntryPosition(ListTypes.ON_DEMAND_LIST, highlight));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flattenedList]);

    const renderSeasonHeader = (season: SeasonGroup): JSX.Element => (
        <div key={`season-${season.seasonNumber}`} className="od-season-header">
            <div className="od-season-title">{season.seasonTitle}</div>
        </div>
    );

    const renderEpisodeItem = (
        episode: EpisodeInfo,
        itemClass: string,
        index: number,
    ): JSX.Element => {
        const isFocused = index === itemPosition;
        return (
            <OnDemandListItem
                key={`episode-${episode.contentId}`}
                episode={episode}
                index={index}
                itemClass={itemClass}
                isSelected={isFocused}
                ghostMode={isFocused && ghostMode}
            />
        );
    };

    const renderListItem = (item: ListItem, itemClass: string, index: number): JSX.Element =>
        isSeasonHeader(item) ? renderSeasonHeader(item) : renderEpisodeItem(item, itemClass, index);

    const headerTitle = `${Locale.getString(LocaleKeys.ON_DEMAND_EPISODES_UNIFIED_COLLECTION_DETAIL)}: ${title}`;
    const emptyMessage = Locale.getString(LocaleKeys.EMPTY_LIST_TEXT);

    // TODO: Handle LoadingState

    return (
        <div className="od-list-container">
            <ScreenHeader title={headerTitle} clockType={ClockTypes.DEFAULT_CLOCK} />
            <div className="od-list-content">
                <div className="od-list-wrapper">
                    {flattenedList && flattenedList.length > 0 && (
                        <List
                            type={ListTypes.ON_DEMAND_LIST}
                            itemList={flattenedList}
                            generateItemView={(item, itemClass, index) =>
                                renderListItem(item, itemClass, index || 0)
                            }
                        />
                    )}
                    {!isLoading && episodes.length === 0 && (
                        <div className="od-empty-list">{emptyMessage}</div>
                    )}
                </div>
            </div>
            <div className={`od-preview-container ${isVideoWindowEnabled ? 'video-enabled' : ''}`}>
                {focusedVodItem && (
                    <VodPreview
                        screenType={VodScreenType.ON_DEMAND_EPISODES_SCREEN}
                        item={focusedVodItem}
                    />
                )}
            </div>
        </div>
    );
};

export default OnDemandList;
